import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ExternalLink, Globe2, RefreshCw, Search, Sparkles, TrendingUp } from "lucide-react";
import { AionSectionCard } from "@/components/aion/AionSectionCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getLiveBriefing, searchLiveBriefing } from "@/services/briefing";

const SCOPES = [
  { id: "world", label: "Welt" },
  { id: "germany", label: "Deutschland" },
  { id: "markets", label: "Märkte" },
  { id: "tech", label: "Tech & AI" },
];

const formatTimestamp = (value) => {
  if (!value) return "Zeitpunkt nicht vorhanden";
  try {
    return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
  } catch {
    return String(value);
  }
};

const sourceAge = (source) => source?.published_at ? formatTimestamp(source.published_at) : "Veröffentlichungszeit nicht vorhanden";

export const BriefingView = ({ token }) => {
  const [scope, setScope] = useState("world");
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("LOADING");
  const [error, setError] = useState("");

  const load = async (nextScope = scope) => {
    setStatus("LOADING");
    setError("");
    try {
      const result = await getLiveBriefing(nextScope, token);
      setData(result);
      setStatus("READY");
    } catch (err) {
      setData(null);
      setError(String(err?.message || "Live Briefing konnte nicht geladen werden."));
      setStatus("ERROR");
    }
  };

  useEffect(() => {
    void load(scope);
    // Scope intentionally owns the live request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, token]);

  const submitSearch = async (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length < 3) return;
    setStatus("LOADING");
    setError("");
    try {
      const result = await searchLiveBriefing(trimmed, token);
      setData(result);
      setStatus("READY");
    } catch (err) {
      setData(null);
      setError(String(err?.message || "Suche konnte nicht geladen werden."));
      setStatus("ERROR");
    }
  };

  const research = data?.research || {};
  const sources = Array.isArray(research.sources) ? research.sources : [];
  const conflicts = Array.isArray(research.conflicts) ? research.conflicts : [];
  const market = data?.market || null;
  const providerLabel = research.provider_state === "AVAILABLE"
    ? (research.cached ? "CACHE" : "LIVE")
    : research.provider_state === "DEGRADED" ? "GESTÖRT" : "NICHT VERBUNDEN";
  const selectedLabel = useMemo(() => SCOPES.find((item) => item.id === scope)?.label || "Suche", [scope]);

  return (
    <div className="aion-view aion-view-stack aion-briefing-view" data-testid="briefing-view">
      <section className="aion-briefing-hero" data-testid="briefing-hero">
        <div className="aion-briefing-copy">
          <p className="aion-section-eyebrow">AION LIVE · READ ONLY</p>
          <h1>Was passiert gerade?</h1>
          <p>Aktuelle öffentliche Quellen, Marktstatus und nachvollziehbare Herkunft – ohne versteckte Aktionen.</p>
          <form className="aion-briefing-search" onSubmit={submitSearch} data-testid="briefing-search-form">
            <Search aria-hidden="true" />
            <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Frag nach einem aktuellen Thema …" data-testid="briefing-search-input" />
            <Button type="submit" disabled={query.trim().length < 3 || status === "LOADING"} data-testid="briefing-search-button">Suchen</Button>
          </form>
          <div className="aion-briefing-tabs" data-testid="briefing-scope-tabs">
            {SCOPES.map((item) => (
              <button
                type="button"
                key={item.id}
                className={scope === item.id ? "is-active" : ""}
                aria-pressed={scope === item.id}
                onClick={() => setScope(item.id)}
                data-testid={`briefing-scope-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <div className="aion-briefing-orb-wrap" aria-hidden="true">
          <div className="aion-briefing-orb">
            <span className="aion-briefing-orbit aion-briefing-orbit-one" />
            <span className="aion-briefing-orbit aion-briefing-orbit-two" />
            <span className="aion-briefing-marker aion-briefing-marker-a" />
            <span className="aion-briefing-marker aion-briefing-marker-b" />
            <span className="aion-briefing-marker aion-briefing-marker-c" />
            <Globe2 />
          </div>
          <div className="aion-briefing-orb-label">{selectedLabel}</div>
        </div>
      </section>

      <div className="aion-compact-status-grid" data-testid="briefing-status-grid">
        <div className="aion-compact-status-box"><span>Quelle</span><strong>{research.provider || "AION Research"}</strong></div>
        <div className="aion-compact-status-box"><span>Status</span><strong>{providerLabel}</strong></div>
        <div className="aion-compact-status-box"><span>Freshness</span><strong>{research.freshness || "—"}</strong></div>
        <div className="aion-compact-status-box"><span>Quellen</span><strong>{sources.length}</strong></div>
        <div className="aion-compact-status-box"><span>Konflikte</span><strong>{conflicts.length}</strong></div>
        <div className="aion-compact-status-box"><span>Aktualisiert</span><strong>{data?.generated_at ? formatTimestamp(data.generated_at) : "—"}</strong></div>
      </div>

      {error ? <div className="aion-error-box" data-testid="briefing-error">{error}</div> : null}

      <AionSectionCard
        title={data?.scope === "search" ? "AION Recherche" : `${selectedLabel} Briefing`}
        eyebrow="LIVE BRIEFING"
        subtitle="Öffentliche Quellen werden als externe, nicht vertrauenswürdige Daten behandelt."
        testId="briefing-summary-card"
        action={<Button variant="outline" onClick={() => void load(scope)} disabled={status === "LOADING"} data-testid="briefing-refresh-button"><RefreshCw size={16} /> Aktualisieren</Button>}
      >
        {status === "LOADING" ? <div className="aion-note" data-testid="briefing-loading">AION prüft aktuelle Quellen …</div> : null}
        {status === "READY" && research.provider_state !== "AVAILABLE" ? (
          <div className="aion-note aion-briefing-provider-note" data-testid="briefing-provider-unavailable">
            <AlertTriangle aria-hidden="true" />
            <div><strong>Livequelle noch nicht verfügbar</strong><p>{research.failure_reason || research.answer || "Der öffentliche Research-Provider ist noch nicht konfiguriert."}</p></div>
          </div>
        ) : null}
        {status === "READY" && research.answer ? <p className="aion-briefing-answer" data-testid="briefing-answer">{research.answer}</p> : null}
      </AionSectionCard>

      {conflicts.length ? (
        <AionSectionCard title="Quellen widersprechen sich" eyebrow="EVIDENZ" subtitle="AION entscheidet den Widerspruch nicht heimlich." testId="briefing-conflicts-card">
          <div className="aion-list">
            {conflicts.map((item, index) => <div className="aion-list-row" key={`${item.topic}-${index}`}><div className="aion-list-copy"><strong>{item.topic}</strong><small>{item.summary}</small></div></div>)}
          </div>
        </AionSectionCard>
      ) : null}

      <AionSectionCard title="Quellen" eyebrow="SOURCE TRACE" subtitle="Öffne die Originalquelle, um Details selbst zu prüfen." testId="briefing-sources-card">
        {sources.length ? (
          <div className="aion-briefing-source-grid" data-testid="briefing-source-grid">
            {sources.map((source, index) => (
              <article className="aion-briefing-source-card" key={`${source.url}-${index}`} data-testid={`briefing-source-${index}`}>
                <div className="aion-briefing-source-meta"><span>{source.domain}</span><span>{sourceAge(source)}</span></div>
                <h3>{source.title}</h3>
                <p>{source.snippet || "Keine Vorschau verfügbar."}</p>
                <a href={source.url} target="_blank" rel="noreferrer noopener">Originalquelle <ExternalLink size={14} aria-hidden="true" /></a>
              </article>
            ))}
          </div>
        ) : <div className="aion-note" data-testid="briefing-sources-empty">Noch keine verlässlichen Livequellen für diese Ansicht verfügbar.</div>}
      </AionSectionCard>

      {data?.scope === "markets" ? (
        <AionSectionCard title="AION Market Intelligence" eyebrow="MARKT · READ ONLY" subtitle="Gespeicherte Marktanalyse bleibt getrennt von öffentlichen Nachrichten. Keine Order-Ausführung." testId="briefing-market-card">
          <div className="aion-compact-status-grid">
            <div className="aion-compact-status-box"><span>Analysen</span><strong>{market?.analyses ?? 0}</strong></div>
            <div className="aion-compact-status-box"><span>Letztes Symbol</span><strong>{market?.latest?.symbol || "—"}</strong></div>
            <div className="aion-compact-status-box"><span>Szenario</span><strong>{market?.latest?.scenario || "—"}</strong></div>
            <div className="aion-compact-status-box"><span>Trend</span><strong>{market?.latest?.trend || "—"}</strong></div>
          </div>
          <div className="aion-note" data-testid="briefing-market-safety"><TrendingUp aria-hidden="true" /> Marktinformationen sind Szenarien und historische Daten, keine sichere Prognose und keine Handelsanweisung.</div>
        </AionSectionCard>
      ) : null}

      <div className="aion-briefing-policy" data-testid="briefing-read-only-policy"><Sparkles aria-hidden="true" /> Diese Seite ist read-only. Nachrichten, Links und Marktansichten können keine Geräte-, Geld- oder Trading-Aktion auslösen.</div>
    </div>
  );
};
